import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LoadingState } from '@bcecloud/portal-ui';
import { hasPermission, readMyIdentity } from '@/lib/events';

export interface RequirePermissionProps {
  /** The permission, or several of which any one admits (a page two audiences share). */
  readonly permission: string | readonly string[];
  readonly children: React.ReactNode;
}

/**
 * Route guard: renders its children only when the signed-in staff member
 * holds the permission, otherwise redirects to the events home.
 *
 * This is a convenience, not the security boundary — the V3 API enforces the
 * same permission on every gated endpoint, so a hand-typed URL that slips
 * past this still gets an empty, error-noticed page rather than data.
 *
 * @param props - The required permission and the guarded page.
 * @returns The page, a loader, or a redirect.
 */
export function RequirePermission({
  permission,
  children,
}: RequirePermissionProps): React.ReactElement {
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });

  if (identity.isPending) return <LoadingState label="Checking access…" />;
  const anyOf: readonly string[] = typeof permission === 'string' ? [permission] : permission;
  if (!anyOf.some((each) => hasPermission(identity.data, each))) {
    return <Navigate to="/events" replace />;
  }
  return <>{children}</>;
}
